"use client";

import { useEffect, useState } from "react";
import {
  Timestamp,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { getDb } from "@/lib/firebase/client";
import { paths, refCol } from "@/lib/db/paths";
import { completionSchema, type Completion } from "@/lib/schemas";

type State = {
  completions: Completion[];
  loading: boolean;
};

const cache = new Map<string, Completion[]>();
const cacheKey = (hid: string, startMs: number, endMs: number) =>
  `week:${hid}:${startMs}:${endMs}`;

/**
 * Wszystkie completions w danym zakresie tygodnia (po dueDate).
 * Realtime + module cache.
 */
export function useWeekCompletions(
  householdId: string | undefined,
  weekStart: Date,
  weekEnd: Date,
): State {
  const startMs = weekStart.getTime();
  const endMs = weekEnd.getTime();
  const key = householdId ? cacheKey(householdId, startMs, endMs) : "";
  const cached = key ? cache.get(key) : undefined;
  const [state, setState] = useState<State>({
    completions: cached ?? [],
    loading: cached === undefined,
  });

  useEffect(() => {
    if (!householdId) return;
    const db = getDb();
    const startTs = Timestamp.fromDate(new Date(startMs));
    const endTs = Timestamp.fromDate(new Date(endMs));
    const q = query(
      refCol(db, paths.completions(householdId)),
      where("dueDate", ">=", startTs),
      where("dueDate", "<=", endTs),
    );
    const k = cacheKey(householdId, startMs, endMs);
    const unsub = onSnapshot(
      q,
      (snap) => {
        const completions = snap.docs.flatMap((d) => {
          const parsed = completionSchema.safeParse({ id: d.id, ...d.data() });
          return parsed.success ? [parsed.data] : [];
        });
        cache.set(k, completions);
        setState({ completions, loading: false });
      },
      () => setState({ completions: [], loading: false }),
    );
    return () => unsub();
  }, [householdId, startMs, endMs]);

  return state;
}
